// hooks/useVideoState.ts - Video state management
import { useState, useRef } from 'react';
import { VideoState } from '../types';

export const useVideoState = () => {
  // All the existing video state in one place
  const [videoState, setVideoState] = useState<VideoState>({
    videoSrc: null,
    videoFileName: '',
    isLoading: false,
    loadingSource: null,
    isPlaying: false,
    currentTime: 0,
    duration: 0,
    playbackRate: 1.0,
    isMirrored: true,
    isMuted: true,
    userMutePreference: false
  });

  const videoRef = useRef<HTMLVideoElement>(null);

  // All the video functions 
  const togglePlay = () => {
    if (videoRef.current) {
      if (videoState.isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setVideoState(prev => ({ ...prev, isPlaying: !prev.isPlaying }));
    }
  };

  const toggleMirror = () => {
    setVideoState(prev => ({ ...prev, isMirrored: !prev.isMirrored }));
  };

  const toggleMute = () => {
    if (videoRef.current) {
      const newMutedState = !videoState.isMuted;
      setVideoState(prev => ({ 
        ...prev, 
        isMuted: newMutedState, 
        userMutePreference: newMutedState 
      }));
      videoRef.current.muted = newMutedState;
    }
  };

  const seekToTime = (time: number) => {
    if (videoRef.current) {
      videoRef.current.currentTime = time;
      setVideoState(prev => ({ ...prev, currentTime: time }));
    }
  };


  const handleVideoLoaded = (allowAutoPlay = true) => {
    setVideoState(prev => ({ ...prev, isLoading: false }));
    if (videoRef.current) {
      setVideoState(prev => ({ 
        ...prev, 
        duration: videoRef.current!.duration
      }));

      videoRef.current.muted = videoState.isMuted;

      // Start playing as soon as a video is picked (upload, library, demo). Browsers may refuse to
      // autoplay with sound; then play muted rather than not at all (one tap on mute brings it back).
      if (allowAutoPlay) {
        const video = videoRef.current;
        video.play().then(() => {
          setVideoState(prev => ({ ...prev, isPlaying: true }));
        }).catch(error => {
          if (error?.name !== 'NotAllowedError' || video.muted) {
            console.log('Autoplay prevented by browser:', error);
            return;
          }
          video.muted = true;
          video.play().then(() => {
            setVideoState(prev => ({ ...prev, isMuted: true, isPlaying: true }));
          }).catch(mutedError => {
            console.log('Autoplay prevented by browser:', mutedError);
          });
        });
      }
    }
  };

  const handleVideoError = () => {
    setVideoState(prev => ({
      ...prev,
      isLoading: false,
      isPlaying: false
    }));
  };

  // Return everything the component needs
  return {
    videoState,
    setVideoState,
    videoRef,
    togglePlay,
    toggleMirror,
    toggleMute,
    seekToTime,
    handleVideoLoaded,
    handleVideoError
  };
};
